/**
 * useOutlookAutoSync
 *
 * While the CRM tab is focused and the user has connected Outlook, silently
 * kick /integrations/outlook/sync every N seconds. Skips when the tab is
 * hidden (matches the browser's own throttling anyway) and pauses on error
 * to avoid hammering Graph.
 *
 * We DELIBERATELY don't do this via a cron every 30s because:
 *  - Cloudflare cron minimum is 1 minute anyway
 *  - Polling only when a user is actively in the CRM saves 90%+ of API calls
 *  - Microsoft Graph rate limits are per-mailbox and per-app; we don't want
 *    to burn the budget when nobody's looking
 */
import { useEffect, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { integrationsApi } from "@/lib/api"

const DEFAULT_INTERVAL_MS = 2 * 60 * 1000 // 2 minutes
const BACKOFF_MS = 10 * 60 * 1000 // 10 min after an error

type Options = {
  enabled: boolean
  intervalMs?: number
}

export function useOutlookAutoSync({ enabled, intervalMs = DEFAULT_INTERVAL_MS }: Options) {
  const qc = useQueryClient()
  const inFlight = useRef(false)
  const nextRunAt = useRef(Date.now())

  useEffect(() => {
    if (!enabled) return

    let stopped = false
    let timer: number | undefined

    const tick = async () => {
      if (stopped) return
      if (document.visibilityState !== "visible") {
        // Skip and reschedule
        schedule(15_000)
        return
      }
      if (inFlight.current) {
        schedule(5_000)
        return
      }
      if (Date.now() < nextRunAt.current) {
        schedule(nextRunAt.current - Date.now())
        return
      }
      inFlight.current = true
      try {
        const r = await integrationsApi.outlookSync()
        if (r.error) {
          // Back off after error
          nextRunAt.current = Date.now() + BACKOFF_MS
        } else {
          nextRunAt.current = Date.now() + intervalMs
          if (r.matched > 0 || r.unmatched > 0) {
            // Refresh timeline + status queries so the new emails appear
            qc.invalidateQueries({ queryKey: ["communications"] })
            qc.invalidateQueries({ queryKey: ["integration-status"] })
          }
        }
      } catch {
        nextRunAt.current = Date.now() + BACKOFF_MS
      } finally {
        inFlight.current = false
        schedule(intervalMs)
      }
    }

    const schedule = (ms: number) => {
      window.clearTimeout(timer)
      timer = window.setTimeout(tick, Math.max(1000, ms))
    }

    // Also re-tick when the tab regains focus
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() >= nextRunAt.current) {
        void tick()
      }
    }
    document.addEventListener("visibilitychange", onVisible)

    // Kick off first run soon
    schedule(3_000)

    return () => {
      stopped = true
      window.clearTimeout(timer)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [enabled, intervalMs, qc])
}
